/**
 * Shared date range for every insights page, synced through the URL search
 * params `range` (24h | 7d | 30d | custom), `from`, `to` (yyyy-mm-dd for custom).
 */
import type { TimeRange } from "./api";

export type RangePreset = "24h" | "7d" | "30d" | "custom";
export const MAX_RANGE_MS = 30 * 24 * 3600_000;
export const RANGE_PRESETS: Array<{ id: Exclude<RangePreset, "custom">; label: string; ms: number }> = [
  { id: "24h", label: "Last 24 hours", ms: 24 * 3600_000 },
  { id: "7d", label: "Last 7 days", ms: 7 * 24 * 3600_000 },
  { id: "30d", label: "Last 30 days", ms: MAX_RANGE_MS },
];

export interface RangeState {
  preset: RangePreset;
  from?: string; // yyyy-mm-dd when custom
  to?: string;
}

export function rangeFromSearch(search: Record<string, unknown>): RangeState {
  const preset = String(search.range ?? "7d") as RangePreset;
  if (preset === "custom") {
    return { preset, from: typeof search.from === "string" ? search.from : undefined, to: typeof search.to === "string" ? search.to : undefined };
  }
  return { preset: RANGE_PRESETS.some((p) => p.id === preset) ? preset : "7d" };
}

export function rangeToSearch(r: RangeState): Record<string, string> {
  if (r.preset === "custom") return { range: "custom", ...(r.from ? { from: r.from } : {}), ...(r.to ? { to: r.to } : {}) };
  return { range: r.preset };
}

function dateValue(d: Date): string {
  return d.toISOString().slice(0, 10);
}

/** Selectable calendar window: retained data only, ending today. */
export function customDateBounds(now: Date = new Date()): { min: string; max: string } {
  return { min: dateValue(new Date(now.getTime() - MAX_RANGE_MS)), max: dateValue(now) };
}

function parsedDate(value: string | undefined, endOfDay: boolean): Date | undefined {
  if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return undefined;
  const d = new Date(`${value}T${endOfDay ? "23:59:59.999" : "00:00:00.000"}Z`);
  return Number.isNaN(d.getTime()) || dateValue(d) !== value ? undefined : d;
}

/** Resolve the state into absolute RFC3339 bounds. `now` is injectable for tests. */
export function resolveRange(r: RangeState, now: Date = new Date()): TimeRange {
  if (r.preset === "custom" && (r.from || r.to)) {
    const retentionFloor = new Date(now.getTime() - MAX_RANGE_MS);
    let to = parsedDate(r.to, true) ?? now;
    if (to > now) to = now;
    if (to < retentionFloor) to = retentionFloor;
    let from = parsedDate(r.from, false) ?? new Date(to.getTime() - 7 * 24 * 3600_000);
    if (from < retentionFloor) from = retentionFloor;
    if (from >= to) from = new Date(Math.max(retentionFloor.getTime(), to.getTime() - 24 * 3600_000));
    return { from: from.toISOString(), to: to.toISOString() };
  }
  const preset = RANGE_PRESETS.find((p) => p.id === r.preset) ?? RANGE_PRESETS[1]!;
  return { from: new Date(now.getTime() - preset.ms).toISOString(), to: now.toISOString() };
}

export function rangeLabel(r: RangeState): string {
  if (r.preset === "custom") return `${r.from ?? "…"} → ${r.to ?? "today"}`;
  return RANGE_PRESETS.find((p) => p.id === r.preset)?.label ?? "Last 7 days";
}
